import React, { useState, useEffect } from "react";
import styled from "styled-components";

import { HyperLink } from "../HyperLink/";
import { Button } from "../Button/";

export interface Props {
  name?: string;
  title: string;
  subtitle?: string;
  hyperLink?: {
    url: string;
    text: string;
  };
  button?: {
    id: string;
    onClick: () => void;
    text: string;
    classes?: string;
    dataTestId: string;
  };
  wide?: boolean;
  variants?: "dark" | string;
  dataTestId?: string;
  backgroundImgURLs?: {
    extraSmallImgURL?: string;
    smallImgURL?: string;
    mediumImgURL?: string;
    largeImgURL?: string;
  };
}

export const getGraphic = (
  screenSize: string,
  backgroundImgURL?: Props["backgroundImgURLs"]
) => {
  switch (screenSize) {
    case "large":
      return (backgroundImgURL?.largeImgURL as string) || null;
    case "medium":
      return (backgroundImgURL?.mediumImgURL as string) || null;
    case "small":
      return (backgroundImgURL?.smallImgURL as string) || null;
    case "extrasmall":
      return (backgroundImgURL?.extraSmallImgURL as string) || null;
    default:
      return (backgroundImgURL?.extraSmallImgURL as string) || null;
  }
};

const HeroContainer = styled.div<{
  $backgroundImageURL: string | null;
}>`
  background-size: auto;
  background-repeat: no-repeat;
  background-position: right 1rem bottom 0rem;
  background-image: ${(props) =>
    props.$backgroundImageURL ? `url("${props.$backgroundImageURL}")` : "none"};
`;

export const HeroPanel = (props: Props) => {
  const [screenSize, setScreenSize] = useState("extrasmall");
  const updateScreenSize = () => {
    const windowWidth = window.innerWidth;
    if (windowWidth > 980) {
      setScreenSize("large");
    } else if (windowWidth >= 740) {
      setScreenSize("medium");
    } else if (windowWidth > 500) {
      setScreenSize("small");
    } else {
      setScreenSize("extrasmall");
    }
  };

  useEffect(() => {
    window.addEventListener("resize", updateScreenSize);
    updateScreenSize();

    return () => {
      window.removeEventListener("resize", updateScreenSize);
    };
  }, []);

  const isDarkVariant = props.variants && props.variants.includes("dark");
  return (
    <section
      className={`ons-hero ons-grid--gutterless ${
        isDarkVariant && "ons-hero--dark"
      }`}
      data-testid={props.dataTestId}
    >
      <HeroContainer
        $backgroundImageURL={getGraphic(screenSize, props.backgroundImgURLs)}
        className={`ons-hero__container ons-container ${
          props.wide && "ons-container--wide"
        }`}
        data-testid="ons-hero-container"
      >
        <div className="ons-hero__details ons-grid__col ons-col-8@m ons-col-10@s@m">
          <header>
            <h1 className="ons-hero__title ons-u-fs-xxxl">
              <span className="ons-u-d-b ons-u-fs-m ons-u-fw-n ons-u-mb-xs">
                {props.name}
              </span>
              {props.title}
            </h1>
          </header>
          <div className="ons-hero__text ons-u-mb-l">
            {props.subtitle}
            {props.subtitle && props.hyperLink && " "}
            {props.hyperLink && (
              <HyperLink
                url={props.hyperLink.url}
                text={props.hyperLink.text}
                classes="ons-list__link"
              />
            )}
          </div>
          {props.button && (
            <Button
              id={props.button.id}
              text={props.button?.text}
              variants={isDarkVariant ? "ghost" : ""}
              iconType="NextArrow"
              iconPosition="after"
              onClick={props.button?.onClick}
              dataTestId={props.button?.dataTestId}
            />
          )}
        </div>
      </HeroContainer>
    </section>
  );
};
